import { Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivate,
  CanActivateChild,
  CanLoad,
  Route,
  Router,
  RouterStateSnapshot,
  UrlSegment,
  UrlTree,
} from '@angular/router';
import { map, Observable, switchMap, take } from 'rxjs';
import { UserCacheService } from '../../user/user-cache.service';
import { OutletCacheService } from '../../outlet-cache/outlet-cache.service';
import { OutletFromUrlService } from '../../outlet-cache/outlet-from-url.service';
import { OutletRightsDTO } from '../../outlet-cache/model/outlet';
import { RedirectUrlService } from '../../helper/redirect-url.service';

@Injectable({
  providedIn: 'root',
})
export class AuthGuard implements CanActivate, CanActivateChild, CanLoad {
  constructor(
    private userService: UserCacheService,
    private outletService: OutletCacheService,
    private outletFromUrlService: OutletFromUrlService,
    private redirectUrlService: RedirectUrlService,
    private router: Router
  ) {}

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ):
    | Observable<boolean | UrlTree>
    | Promise<boolean | UrlTree>
    | boolean
    | UrlTree {
    return this.canVisit(route.data?.['rights']);
  }
  canActivateChild(
    childRoute: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ):
    | Observable<boolean | UrlTree>
    | Promise<boolean | UrlTree>
    | boolean
    | UrlTree {
    return this.canVisit(childRoute.data?.['rights']);
  }
  canLoad(
    route: Route,
    segments: UrlSegment[]
  ):
    | Observable<boolean | UrlTree>
    | Promise<boolean | UrlTree>
    | boolean
    | UrlTree {
    return this.canVisit(route.data?.['rights']);
  }

  canVisit(rightsNeeded: string[]): Observable<boolean> {
    //the rights to check are the ones of the outlet the url asks for, so it is applied
    //first. Sibling guards are subscribed to in parallel, which is why this is chained
    //here rather than run as a guard of its own next to this one.
    return this.outletFromUrlService.applyOutletFromUrl().pipe(
      switchMap(() => this.outletService.getSelectedOutlet()),
      take(1),
      map((selectedOutlet: OutletRightsDTO | null) => {
        //a link can be opened before any outlet was picked, and the rights to check are the
        //ones of that outlet, so the dashboard asks for one first and brings the user back
        if (!selectedOutlet) {
          this.redirectUrlService.rememberCurrentNavigation();
          this.router.navigate(['/dashboard']);
          return false;
        }

        let allow: boolean = false;
        allow =
          selectedOutlet.rights.find((right) =>
            rightsNeeded.includes(right.name)
          ) !== undefined;

        if (!allow) {
          this.router.navigate(['/forbidden']);
        }

        return allow;
      })
    );
  }
}
